import React from 'react';
import { Activity } from '#constants/types.ts';
import { useIntl } from '@leagueplatform/locales';
import { track } from '@leagueplatform/analytics';
import { CloseButton } from '#components/close-button/close-button.view.tsx';
import { Box, styled, TextAction } from '@leagueplatform/genesis-core';
import { BadgeProgress } from '../badge-progress/badge-progress.view';

interface ProgressBannerProps {
  activities: Activity[];
  onlyProgress: boolean;
  showMoreActivities: Function;
  onClose: Function;
}

const ToastButton = styled(TextAction, {
  display: 'inline-block',
  borderTopWidth: '$thin',
  borderTopStyle: 'solid',
  borderTopColor: '$interactiveBorderDisabled',
  width: '100%',
  padding: '$half',
  textAlign: 'center',
  '&:hover, &:active, &:focus': {
    backgroundColor: '$interactiveBackgroundHovered',
    color: '$interactiveActionHovered',
    textDecoration: 'none',
  },
});

const AbsoluteWrapper = styled(Box, {
  position: 'absolute',
  insetInlineEnd: '$quarter',
  top: '$quarter',
});

const BannerContainer = styled(Box, {
  position: 'relative',
  backgroundColor: '$warningBackgroundSecondary',
  width: '330px',
  boxShadow: '$dropdown',
});

export const ToastProgressBanner = ({
  onlyProgress,
  activities,
  showMoreActivities,
  onClose,
}: ProgressBannerProps) => {
  const { formatMessage } = useIntl();

  const handleSeeMoreAchievements = () => {
    track('rewards.button_clicked.progress_toast.see_more');
    showMoreActivities();
  };

  return (
    <BannerContainer
      css={{
        borderRadius: onlyProgress ? '$large' : '$none $none $large $large',
      }}
    >
      <BadgeProgress
        imageUrl={activities[0]?.image.large}
        completed={activities[0]?.progress.is_completed}
        title={activities[0]?.name}
        isLoading={false}
        currentActivity={activities[0]?.progress.current}
        totalActivities={activities[0]?.progress.threshold}
        progressTitle={`${activities[0]?.progress.current}/${
          activities[0]?.progress.threshold
        } ${formatMessage({ id: 'ACTIVITIES_PROGRESS' })}`}
      />
      {activities.length > 1 && (
        <ToastButton onClick={handleSeeMoreAchievements}>
          {formatMessage({ id: 'SEE_ALL_ACHIEVEMENTS' })}
        </ToastButton>
      )}
      {onlyProgress && (
        <AbsoluteWrapper>
          <CloseButton onClose={onClose} />
        </AbsoluteWrapper>
      )}
    </BannerContainer>
  );
};
